import React from 'react';
import {
  DatePicker,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  SearchableSelect,
} from '@jem-open/jem-ui';
import {
  MonthlyType,
  RepeatFrequency,
  useMultiProductRecon,
} from '../MultiProductReconContext';

const WEEK_DAYS = [
  { value: 'MO', label: 'Monday' },
  { value: 'TU', label: 'Tuesday' },
  { value: 'WE', label: 'Wednesday' },
  { value: 'TH', label: 'Thursday' },
  { value: 'FR', label: 'Friday' },
];

const MONTH_DATES = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const NTH_DAY_IN_MONTH = [
  { value: '1', label: '1st' },
  { value: '2', label: '2nd' },
  { value: '3', label: '3rd' },
  { value: '4', label: '4th' },
  { value: '-1', label: 'Last' },
];

const RecurrenceForm = () => {
  const { state, updateSchedule } = useMultiProductRecon();
  const { schedule } = state;

  const handleIntervalChange = (raw: string) => {
    if (raw === '') {
      updateSchedule({ interval: null });
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 1 || n > 4) {
      updateSchedule({ interval: null });
      return;
    }
    updateSchedule({ interval: n });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label>Repeat</Label>
        <RadioGroup
          value={schedule.repeatFrequency ?? ''}
          onValueChange={(value) =>
            updateSchedule({
              repeatFrequency: (value || null) as RepeatFrequency | null,
            })
          }
          className="flex items-center gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="weekly" id="mp-repeat-weekly" />
            <Label
              htmlFor="mp-repeat-weekly"
              className="font-normal !leading-none"
            >
              Weekly
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="monthly" id="mp-repeat-monthly" />
            <Label
              htmlFor="mp-repeat-monthly"
              className="font-normal !leading-none"
            >
              Monthly
            </Label>
          </div>
        </RadioGroup>
      </div>

      {schedule.repeatFrequency === 'weekly' && (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="mp-interval">
              Every (number of{' '}
              {schedule.interval && schedule.interval > 1 ? 'weeks' : 'week'})
            </Label>
            <Input
              id="mp-interval"
              type="number"
              min={1}
              max={4}
              value={schedule.interval ?? ''}
              onChange={(e) => handleIntervalChange(e.target.value)}
              className="w-32"
            />
          </div>

          {schedule.interval !== null &&
            schedule.interval > 0 &&
            schedule.interval <= 2 && (
              <div className="space-y-1">
                <Label>On</Label>
                <RadioGroup
                  value={schedule.dayOfWeek ?? ''}
                  onValueChange={(value) =>
                    updateSchedule({ dayOfWeek: value || null })
                  }
                  className="flex flex-wrap items-center gap-x-6 gap-y-2"
                >
                  {WEEK_DAYS.map((day) => (
                    <div
                      key={day.value}
                      className="flex items-center space-x-2"
                    >
                      <RadioGroupItem
                        value={day.value}
                        id={`mp-day-${day.value}`}
                      />
                      <Label
                        htmlFor={`mp-day-${day.value}`}
                        className="font-normal !leading-none"
                      >
                        {day.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}
        </div>
      )}

      {schedule.repeatFrequency === 'monthly' && (
        <div className="space-y-3">
          <RadioGroup
            value={schedule.monthlyType}
            onValueChange={(value) =>
              updateSchedule({ monthlyType: value as MonthlyType })
            }
            className="space-y-2"
          >
            <div className="flex items-center gap-3">
              <RadioGroupItem value="byDateInMonth" id="mp-monthly-by-date" />
              <Label
                htmlFor="mp-monthly-by-date"
                className="min-w-[5rem] font-normal !leading-none"
              >
                On day
              </Label>
              <div className="w-40">
                <SearchableSelect
                  options={MONTH_DATES}
                  value={
                    schedule.dateInMonth !== null
                      ? String(schedule.dateInMonth)
                      : ''
                  }
                  onValueChange={(value) =>
                    updateSchedule({
                      dateInMonth: value ? Number(value) : null,
                    })
                  }
                  disabled={schedule.monthlyType !== 'byDateInMonth'}
                  placeholder="Day"
                  searchPlaceholder="Search..."
                  emptyMessage="No match."
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <RadioGroupItem value="nthDayInMonth" id="mp-monthly-by-nth" />
              <Label
                htmlFor="mp-monthly-by-nth"
                className="min-w-[5rem] font-normal !leading-none"
              >
                On the
              </Label>
              <div className="w-40">
                <SearchableSelect
                  options={NTH_DAY_IN_MONTH}
                  value={
                    schedule.nthDayInMonth !== null
                      ? String(schedule.nthDayInMonth)
                      : ''
                  }
                  onValueChange={(value) =>
                    updateSchedule({
                      nthDayInMonth: value ? Number(value) : null,
                    })
                  }
                  disabled={schedule.monthlyType !== 'nthDayInMonth'}
                  placeholder="Position"
                  searchPlaceholder="Search..."
                  emptyMessage="No match."
                />
              </div>
              <div className="w-40">
                <SearchableSelect
                  options={WEEK_DAYS}
                  value={schedule.posDayInMonth ?? ''}
                  onValueChange={(value) =>
                    updateSchedule({ posDayInMonth: value || null })
                  }
                  disabled={schedule.monthlyType !== 'nthDayInMonth'}
                  placeholder="Day"
                  searchPlaceholder="Search..."
                  emptyMessage="No match."
                />
              </div>
            </div>
          </RadioGroup>
        </div>
      )}

      <div className="space-y-1">
        <Label>Start</Label>
        <DatePicker
          date={schedule.startDate ?? undefined}
          onDateChange={(d) => updateSchedule({ startDate: d ?? null })}
          placeholder="Pick a start date"
        />
        <p className="text-xs text-muted-foreground">
          The first recon will run on the first occurrence of the recurrence
          pattern above on or after this date. E.g. picking the 10th when the
          schedule fires on the 12th means the first run is on the 12th.
        </p>
      </div>
    </div>
  );
};

export default RecurrenceForm;
